import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { ATLAS_MAX_ADDRESSES_PER_RUN } from '@infras/shared-types';
import { timeAgo } from '../../lib/agentCommands';
import {
  isRestorePreTicked,
  RESTORE_CONFIRM,
  RESTORE_PREVIEW,
  restoreOutcomeLabel,
  type AtlasRestoreResult,
  type AtlasRestoreRow,
  type AtlasRestoreTarget,
} from '../../lib/agentLifecycle';

/**
 * Restore an address to an Atlas access list — the human counterpart of Remove.
 *
 * IT DELIBERATELY MIRRORS `AgentRemoveDialog`: preview → tick → confirm → four outcome
 * classes → result, root-gated, with the same scrollable geometry (the preview scrolls in
 * its own box, the actions stay pinned inside the card). Two dialogs that do opposite
 * things to the same production access list should be ONE thing to learn, not two, so the
 * layout, the button grammar ("Keep it" / "Done") and the outcome vocabulary are the same
 * shape. Where they differ, they differ because the question differs — see below.
 *
 * WHY IT EXISTS. Nothing else brings an address back: `atlasHealthy` is sticky-true so a
 * healthy agent never re-registers, reconcile is add-only and off in production, and the
 * owner ruled out automatic repair (Binh, 2026-08-25: "Recover or Prune or Clean up will
 * be DevOps job"). Before this, a wrongly-reclaimed address left a live node off the
 * database with no way back through this app.
 *
 * THE SUBJECT IS THE ADDRESS, NOT THE AGENT. A removal starts from "this node is dead, what
 * does it hold"; a restore starts from "this address is missing, who does it belong to".
 * So the table is one flat list keyed on (project, address), and the agents behind a row are
 * EVIDENCE in a column rather than the grouping.
 *
 * PROVENANCE IS THE POINT (`prunedBy` / `prunedAt` / `prunedReason`). "Someone deleted this
 * deliberately 40 minutes ago" and "Atlas lost it and nobody knows how" are the same symptom
 * and completely different decisions, and an operator who cannot tell them apart is guessing.
 * Every row shows who, when and why — and says so explicitly when there is no record, rather
 * than leaving a blank cell to be read as "nobody".
 *
 * PRE-TICKED IS `would_restore` AND NOTHING ELSE. That is the server's own affirmative
 * answer. A refused row (including the unreadable-list case) and a capped row arrive
 * unticked with the reason beside them. Unlike Remove, the preview here already carries a
 * per-row verdict, so the operator is un-ticking a proposal rather than assembling one.
 */
export function AtlasRestoreDialog({
  targets,
  label,
  onClose,
  onRestored,
}: {
  /** (project, address) pairs — from the drift rows, or one fleet row's addresses. */
  targets: AtlasRestoreTarget[];
  label: string;
  onClose: () => void;
  /** Fired after a real run so the caller can drop its selection. */
  onRestored?: () => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<AtlasRestoreResult | null>(null);

  const [preview, previewState] = useMutation<{ restoreAgentAddresses: AtlasRestoreResult }>(
    RESTORE_PREVIEW,
    {
      onCompleted: (d) => {
        const next = new Set<string>();
        for (const r of d.restoreAgentAddresses.rows) if (isRestorePreTicked(r)) next.add(key(r));
        setTicked(next);
      },
    },
  );

  const [confirm, confirmState] = useMutation<{ restoreAgentAddresses: AtlasRestoreResult }>(
    RESTORE_CONFIRM,
    {
      // A restore invalidates the same surfaces a removal does, plus the checklist that
      // sent the operator here — and `reactivatedAgentIds` means the FLEET changes too, so
      // `Agents` has to be re-read or the row they came from still reads `removed`.
      refetchQueries: [
        'Agents',
        'AtlasEgressIps',
        'AtlasProjectsOverview',
        'NavCounts',
        'AgentChecklist',
      ],
      awaitRefetchQueries: true,
      onCompleted: (d) => {
        setResult(d.restoreAgentAddresses);
        onRestored?.();
      },
    },
  );

  useEffect(() => {
    void preview({ variables: { targets } });
    // The subject is fixed for the dialog's lifetime, so this runs once on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    cardRef.current?.focus();
    // CAPTURE phase, and it stops the event — same reason as the remove dialog: this can
    // render inside a drawer that also closes on Escape, and one Escape must close exactly
    // one layer.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || confirmState.loading) return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose, confirmState.loading]);

  const rows = previewState.data?.restoreAgentAddresses.rows ?? [];
  const picked = useMemo<AtlasRestoreTarget[]>(
    () =>
      rows
        .filter((r) => ticked.has(key(r)))
        .map((r) => ({ atlasProjectKey: r.atlasProjectKey, ipCidr: r.ipCidr })),
    [rows, ticked],
  );
  // Same cap note as Remove, and the same reason for showing it BEFORE the click: the
  // server defers the excess rather than dropping it, and "some of what I ticked silently
  // did not happen" is the reading this sentence prevents.
  const overCap = picked.length > ATLAS_MAX_ADDRESSES_PER_RUN;
  const alreadyThere = rows.filter((r) => r.inLiveList === true).length;

  const toggle = (k: string) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  return (
    <div
      className="confirm-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !confirmState.loading) onClose();
      }}
    >
      <div
        ref={cardRef}
        className="confirm-card atlas-prune-card agent-remove-card"
        role="dialog"
        aria-modal="true"
        aria-label={`Restore ${label}`}
        tabIndex={-1}
      >
        <h3 className="confirm-title">Restore {label}?</h3>

        {result ? (
          <RestoreResult result={result} onClose={onClose} />
        ) : (
          <>
            <p className="confirm-body">
              Putting an address back on an Atlas access list lets the nodes behind it reach the
              database again. Check who removed it and why before you undo that — and if an agent
              was marked removed by a cascade, restoring its address marks it active again.
            </p>

            <h4 className="section">
              Missing from Atlas
              {rows.length > 1 && (
                <span className="muted">
                  {' '}
                  — {rows.length} address{rows.length === 1 ? '' : 'es'}
                </span>
              )}
            </h4>

            {previewState.loading ? (
              <p className="muted">Reading what Atlas has…</p>
            ) : previewState.error ? (
              <>
                <p className="error">Couldn&apos;t build the preview — the API didn&apos;t respond.</p>
                <p className="muted">
                  Nothing was restored. The preview is the only way in, so there is no half-done
                  state to clean up — close this and try again.
                </p>
              </>
            ) : !rows.length ? (
              <p className="muted">
                Nothing to restore — no address in this set is missing from its access list.
              </p>
            ) : (
              <>
                <div className="agent-remove-preview">
                  <table className="atlas-table">
                    <thead>
                      <tr>
                        <th title="Untick a row to leave that address off Atlas">Restore</th>
                        <th>Atlas project</th>
                        <th>Address</th>
                        <th title="The agents on record as having put this address there, and the newest heartbeat among them">
                          Agent · last heartbeat
                        </th>
                        <th title="Who took this address off the list, when, and why">
                          Removed by
                        </th>
                        <th>What would happen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const k = key(r);
                        const restorable = isRestorePreTicked(r);
                        return (
                          <tr key={k}>
                            <td>
                              <input
                                type="checkbox"
                                checked={ticked.has(k)}
                                disabled={!restorable}
                                onChange={() => toggle(k)}
                                aria-label={`Restore ${r.ipCidr} to ${r.atlasProjectKey}`}
                              />
                            </td>
                            <td>{r.atlasProjectKey}</td>
                            <td className="mono">{r.ipCidr}</td>
                            <td>
                              {r.agentIds.length ? (
                                <>
                                  <div className="agent-remove-age">
                                    {timeAgo(r.agentLastSeenAt)}
                                  </div>
                                  <div className="atlas-sub muted">{r.agentIds.join(', ')}</div>
                                  {/* `removed` is the loud case: a cascade settled this
                                      agent while its node kept beating, so the fleet page
                                      is currently wrong about it. Restoring undoes both. */}
                                  {r.agentStatus === 'removed' && (
                                    <div className="atlas-sub">
                                      <span
                                        className="badge failed"
                                        title="A cascade marked this agent removed while its node kept heartbeating. Restoring the address marks the agent active again."
                                      >
                                        agent marked removed
                                      </span>
                                    </div>
                                  )}
                                </>
                              ) : (
                                <span className="atlas-sub muted">no agent row survives</span>
                              )}
                            </td>
                            <td>
                              {/* Never a blank cell here. An empty "removed by" reads as
                                  "nobody", which is a claim — and the opposite decision
                                  from "we have no record of who did this". */}
                              {r.prunedBy || r.prunedAt ? (
                                <>
                                  <div>{r.prunedBy || 'unknown operator'}</div>
                                  <div className="atlas-sub muted">{timeAgo(r.prunedAt)}</div>
                                  {r.prunedReason && (
                                    <div className="atlas-sub">{r.prunedReason}</div>
                                  )}
                                </>
                              ) : (
                                <span className="atlas-sub muted">
                                  no removal on record — Atlas lost it
                                </span>
                              )}
                            </td>
                            <td>
                              <span
                                className={restorable ? 'atlas-sub' : 'atlas-sub muted'}
                                title={r.reason ?? undefined}
                              >
                                {restoreOutcomeLabel(r.outcome)}
                                {!restorable && r.reason ? ` — ${r.reason}` : ''}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {overCap && (
                  <p className="muted text-xs">
                    More than {ATLAS_MAX_ADDRESSES_PER_RUN} addresses are ticked. The run is capped
                    at {ATLAS_MAX_ADDRESSES_PER_RUN} — the rest come back as{' '}
                    <span className="mono">deferred_cap</span>, are <strong>not</strong> restored,
                    and stay ticked-able: reopen this dialog after the run and confirm again to take
                    the rest. Nothing is silently dropped.
                  </p>
                )}

                {alreadyThere > 0 && (
                  // Still offered, and still ticked — because the Atlas half is the no-op
                  // here, not the whole row. Restoring one of these clears the removal
                  // record and marks a cascaded agent active again, which is the reading
                  // "nothing would happen" would hide.
                  <p className="muted text-xs">
                    {alreadyThere} address{alreadyThere === 1 ? ' is' : 'es are'} already on the
                    list, so nothing is sent to Atlas for{' '}
                    {alreadyThere === 1 ? 'it' : 'them'} — restoring{' '}
                    {alreadyThere === 1 ? 'it' : 'them'} still clears the removal record here and
                    marks any agent a cascade removed active again.
                  </p>
                )}
              </>
            )}

            <div className="btn-row confirm-actions">
              <button
                className="btn btn-danger"
                disabled={
                  confirmState.loading ||
                  previewState.loading ||
                  !!previewState.error ||
                  picked.length === 0
                }
                onClick={() => void confirm({ variables: { targets: picked } })}
              >
                {confirmState.loading
                  ? 'Restoring…'
                  : picked.length
                    ? `Restore ${picked.length} address${picked.length === 1 ? '' : 'es'}`
                    : 'Nothing ticked'}
              </button>
              {/* "Keep it off" rather than "Cancel", for the reason Remove says "Keep it":
                  the safe button should name the state it leaves behind. */}
              <button className="btn btn-ghost" disabled={confirmState.loading} onClick={onClose}>
                Keep it off
              </button>
            </div>
            {confirmState.error && <p className="error">{confirmState.error.message}</p>}
          </>
        )}
      </div>
    </div>
  );
}

const key = (r: { atlasProjectKey: string; ipCidr: string }) => `${r.atlasProjectKey}|${r.ipCidr}`;

/**
 * The outcomes, grouped — and `refused` and `failed` stay apart, exactly as they do on the
 * removal result and for the same reason: `refused` is OUR verdict that the address should
 * stay off, `failed` is an add Atlas rejected. Both leave the address missing; only one of
 * them is finished work.
 */
function RestoreResult({ result, onClose }: { result: AtlasRestoreResult; onClose: () => void }) {
  const by = (...kinds: string[]) => result.rows.filter((r) => kinds.includes(r.outcome));
  const restored = by('restored');
  const failed = by('failed');
  const refused = by('refused');
  const deferred = by('deferred_cap');

  return (
    <div className={`atlas-prune-result${failed.length > 0 ? ' atlas-prune-result--failed' : ''}`}>
      {/* Everything that grows with the run scrolls; `Done` stays outside it. */}
      <div className="agent-remove-outcomes">
        <strong>
          {restored.length} restored
          {failed.length > 0 && ` · ${failed.length} failed`}
          {refused.length > 0 && ` · ${refused.length} refused`}
          {deferred.length > 0 && ` · ${deferred.length} deferred`}
        </strong>

        {/* The fleet changes underneath the operator, so say it plainly rather than letting
            them discover it. This is the cascade's settle being undone. */}
        {result.reactivatedAgentIds.length > 0 && (
          <p className="muted">
            <strong>
              {result.reactivatedAgentIds.length} agent
              {result.reactivatedAgentIds.length === 1 ? ' is' : 's are'} active again
            </strong>{' '}
            — {result.reactivatedAgentIds.join(', ')}.{' '}
            {result.reactivatedAgentIds.length === 1 ? 'It was' : 'They were'} marked removed by a
            cascade while {result.reactivatedAgentIds.length === 1 ? 'its node was' : 'their nodes were'}{' '}
            still heartbeating; the fleet list now shows{' '}
            {result.reactivatedAgentIds.length === 1 ? 'it' : 'them'} as active.
          </p>
        )}

        {failed.length > 0 && (
          <details open>
            <summary>Atlas rejected the add — still missing, retry</summary>
            <ul>
              {failed.map((r) => (
                <li key={key(r)}>
                  <span className="mono">{r.ipCidr}</span> in {r.atlasProjectKey}
                  {r.reason ? `: ${r.reason}` : ''}
                </li>
              ))}
            </ul>
          </details>
        )}
        {deferred.length > 0 && (
          <p className="muted">
            {deferred.length} address{deferred.length === 1 ? '' : 'es'} hit the{' '}
            {ATLAS_MAX_ADDRESSES_PER_RUN}-address cap and {deferred.length === 1 ? 'was' : 'were'}{' '}
            not attempted. Nothing is lost — close this, open Restore again and confirm to take the
            rest.
          </p>
        )}
        {refused.length > 0 && (
          <details open>
            <summary>Refused — left off on purpose</summary>
            <ul>
              {refused.map((r) => (
                <li key={key(r)} title={r.reason ?? undefined}>
                  <span className="mono">{r.ipCidr}</span> in {r.atlasProjectKey} —{' '}
                  {r.reason || restoreOutcomeLabel(r.outcome)}
                </li>
              ))}
            </ul>
          </details>
        )}
        {restored.length > 0 && (
          <details>
            <summary>Put back on the access list</summary>
            <ul>
              {restored.map((r) => (
                <li key={key(r)}>
                  <span className="mono">{r.ipCidr}</span> in {r.atlasProjectKey}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>

      <div className="btn-row confirm-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}
